import { Microfrontend } from '@cian/mf-react';
import { type IMicrofrontendManifest } from '@cian/mf-registry/shared';

import { noop } from 'lodash';
import * as React from 'react';

import { useAdsDisabled } from '../../../hooks/useAdsDisabled';
import { useApplicationContext } from '../../../utils/applicationContext';
import { AD_BANNER_MF_MANIFEST_SETTING_KEY } from '../../../constants/microfrontend';

type TMicrofrontendElement = React.ElementRef<typeof Microfrontend>;

/**
 * Монтирует микрофронтенд строго на клиенте: рекламу нельзя рендерить на SSR.
 *
 * `isReady` поднимается в `true` только после полной загрузки страницы (`window.load`),
 * как это делал легаси-баннер через `useAdsDisabled`/`usePageLoaded` (CD-280813):
 * runtime МКФ, context.js и креатив не должны конкурировать за сеть и main thread
 * с контентом страницы (рекомендации, фильтры) на старте. Дополнительный бонус —
 * к этому моменту антиадблок-детектор уже выставил `window._cianCanRunAds`,
 * и `getCanRunAds` внутри МКФ отрабатывает так же, как в легаси.
 *
 * Второе условие — микрофронтенд есть в браузерном реестре. Регистрирует его
 * `registerAdBannerMicrofrontend` на бутстрапе, один раз на страницу (на сервере манифест
 * намеренно не регистрируется, иначе ассеты МКФ уедут в <head>). Проверка нужна на случай,
 * когда регистрация упала: без неё `<cian-mf-ui>` свалится в `connectedCallback`.
 * На SSR реестр пустой, но там и `isAdsDisabled === true`, так что плейсхолдер отрисуется.
 *
 * Место под баннер до маунта держит плейсхолдер (без CLS).
 */
export function useAdBannerMicrofrontend() {
  const { config, microfrontendsRegistry } = useApplicationContext();

  const microfrontendRef = React.useRef<TMicrofrontendElement | null>(null);

  const isAdsDisabled = useAdsDisabled(noop, []);

  const manifest = config.get<IMicrofrontendManifest>(AD_BANNER_MF_MANIFEST_SETTING_KEY);
  const isRegistered = Boolean(manifest && microfrontendsRegistry.microfrontend(manifest.microfrontendName));

  return { microfrontendRef, isReady: !isAdsDisabled && isRegistered };
}
